import { useQuery } from '@apollo/client';
import { graphql, useStaticQuery } from 'gatsby';
import { isNil, not } from 'ramda';
import React, { FC, useContext, useEffect, useState } from 'react';

import {
	IMedInfo,
	IRxCategoryRouteAndMeds,
	getCategoryMeds,
	removeUnavailableMedications,
} from '@ui/templates/MedicationSelection';

import { EstrogenRoutes } from '@ui/routes/MemberTestosterone/TestosteroneHelpers';

import usePrefersReducedMotion from '@hooks/usePrefersReducedMotion';

import { keysToCamel } from '@utils/butter';
import { doesNotExist, exists } from '@utils/general';

import FlowContext from '@providers/FlowContextProvider';

import { FormData, IFlowStepProps } from '@folx-types/flow-steps';

import rxRoute from '@graphql/queries/rxRoute.gql';

import MedicationSelectionStep, {
	IMedicationSelectionStepPageContent,
} from './MemberTestosteroneMedicationSelectionStep.component';

interface IMedicationSelectionStepPageProps extends IFlowStepProps {}

const MemberTestosteroneMedicationSelectionStepPage: FC<IMedicationSelectionStepPageProps> = ({
	path,
	stepKey,
}) => {
	console.log('MemberTestosteroneMedicationSelectionStep.page');
	const { pageContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "testosterone-medication-selection" }) {
				page_header
				page_details
				routes {
					route_header
					route_types {
						label
						description
						key
					}
				}
				injectables {
					injectable_medication_header
					injectable_type_header
					injectable_types {
						label
						description
						key
					}
					injectable_medications {
						label
						description
						key
					}
				}
			}
		}
	`);

	const [medData, setMedData] = useState<FormData>({});
	const [currentMed, setCurrentMed] = useState<IMedInfo>({});
	const [routeMeds, setRouteMeds] = useState<string[] | undefined>(undefined);

	const { existingFormData, destinationFlow } = useContext(FlowContext);

	const refinedContent = keysToCamel(pageContent) as IMedicationSelectionStepPageContent;
	console.log(`refinedContent`, JSON.stringify(refinedContent));

	const categoryKey: EstrogenRoutes = 'hrt_t';

	const { data: rxRouteData, error: rxRouteError } = useQuery<IRxCategoryRouteAndMeds>(rxRoute, {
		variables: {
			rxCategoryKey: categoryKey,
		},
	});

	useEffect(() => {
		if (isNil(rxRouteError)) {
			return;
		}
	}, [rxRouteError]);

	useEffect(() => {
		if (doesNotExist(rxRouteData)) {
			return;
		}
		if (exists(routeMeds)) {
			return;
		}

		const categoryMeds = getCategoryMeds(
			'new_hrt_t',
			existingFormData?.membershipType
		)(rxRouteData?.rxCategoryRoutes.edges ?? []);

		const availableMeds = removeUnavailableMedications(categoryMeds, existingFormData);

		setRouteMeds(availableMeds);
	}, [rxRouteData]);

	const preferReducedMotion = usePrefersReducedMotion();

	return (
		<MedicationSelectionStep
			path={path}
			stepKey={stepKey}
			pageContent={refinedContent}
			routeMeds={routeMeds}
			rxRouteData={rxRouteData}
			shouldAnimate={not(preferReducedMotion)}
			loading={false}
			handleMedSelection={(data, info) => {
				setMedData(data);
				setCurrentMed(info);
			}}
		/>
	);
};

export default MemberTestosteroneMedicationSelectionStepPage;
